import React from "react";
import "./TablePaginationFooter.css";

export interface TablePaginationFooterProps {
  page: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  pageSizeOptions?: number[];
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

const TablePaginationFooter: React.FC<TablePaginationFooterProps> = ({
  page,
  totalPages,
  pageSize,
  totalCount,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageChange,
  onPageSizeChange,
}) => {
  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    onPageChange?.(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = Number(event.target.value);
    onPageSizeChange?.(newSize);
  };

  const nextPage = page + 1 <= totalPages ? page + 1 : null;
  const showEllipsis = totalPages > 2 && page + 2 < totalPages;

  return (
    <div className="table-pagination-footer">
      <div className="table-pagination-footer-left">
        <span className="table-pagination-footer-text">Showing results</span>
        <select
          className="table-pagination-footer-select"
          value={pageSize}
          onChange={handlePageSizeChange}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <span className="table-pagination-footer-text">of {totalCount}</span>
      </div>
      <div className="table-pagination-footer-right">
        <span className="table-pagination-footer-label">Page</span>
        <button
          type="button"
          className="table-pagination-footer-btn table-pagination-footer-btn-icon"
          disabled={page === 1}
          onClick={() => handlePageChange(1)}
          aria-label="First page"
        >
          <i className="ph-caret-double-left" />
        </button>
        <button
          type="button"
          className="table-pagination-footer-btn table-pagination-footer-btn-icon"
          disabled={page === 1}
          onClick={() => handlePageChange(page - 1)}
          aria-label="Previous page"
        >
          <i className="ph-caret-left" />
        </button>
        <span className="table-pagination-footer-current">{page}</span>
        {nextPage !== null && (
          <button
            type="button"
            className="table-pagination-footer-btn"
            onClick={() => handlePageChange(nextPage)}
          >
            {nextPage}
          </button>
        )}
        {showEllipsis && (
          <span className="table-pagination-footer-ellipsis">…</span>
        )}
        <button
          type="button"
          className="table-pagination-footer-btn table-pagination-footer-btn-icon"
          disabled={page === totalPages}
          onClick={() => handlePageChange(page + 1)}
          aria-label="Next page"
        >
          <i className="ph-caret-right" />
        </button>
        <button
          type="button"
          className="table-pagination-footer-btn table-pagination-footer-btn-icon"
          disabled={page === totalPages}
          onClick={() => handlePageChange(totalPages)}
          aria-label="Last page"
        >
          <i className="ph-caret-double-right" />
        </button>
        <span className="table-pagination-footer-of">of {totalPages}</span>
      </div>
    </div>
  );
};

export default TablePaginationFooter;
